function humanizeToken(value: string): string {
    return value
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/-/g, ' ')
        .split(' ')
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

interface SectionLabelInput {
    label: string;
    analyticsLabel?: string;
}

/**
 * Resolves a stable English analytics label for sidebar items.
 * Prefers explicit analyticsLabel; falls back to plain English labels or humanized tokens.
 */
export function resolveAnalyticsSectionLabel({ label, analyticsLabel }: SectionLabelInput): string {
    if (analyticsLabel) {
        return analyticsLabel;
    }

    if (!label) {
        return '';
    }

    if (!label.includes('.') && /^[A-Z]/.test(label)) {
        return label;
    }

    const lastSegment = label.split('.').pop() ?? label;
    return humanizeToken(lastSegment);
}

/**
 * Derives readable section names from URL path segments when sidebar lookup misses.
 */
export function buildFallbackPageContextFromSuffix(
    suffix: string,
    levelId: string
): Pick<PageContextShape, 'section_id' | 'module_id' | 'main_section' | 'sub_page'> {
    const segments = suffix.split('/').filter(Boolean);

    if (segments.length === 0) {
        const mainSection = humanizeToken(levelId);
        return {
            section_id: levelId,
            module_id: levelId,
            main_section: mainSection,
            sub_page: mainSection,
        };
    }

    const moduleSegment = segments[0];
    const sectionId = `${levelId}-${segments.join('-')}`;
    const moduleId = `${levelId}-${moduleSegment}`;
    const mainSection = humanizeToken(moduleSegment);

    return {
        section_id: sectionId,
        module_id: moduleId,
        main_section: mainSection,
        sub_page: segments.length > 1 ? humanizeToken(segments.slice(1).join('-')) : mainSection,
    };
}

interface PageContextShape {
    section_id: string;
    module_id: string;
    main_section: string;
    sub_page: string;
}
